/**
 * Deep cloning utility with fallback support for older browsers
 * 
 * Uses the native `structuredClone` API when available (modern browsers),
 * falling back to `JSON.parse(JSON.stringify())` for older browser support.
 * 
 * @param obj - The object to deep clone
 * @returns A deep copy of the input object
 * 
 * @example
 * ```typescript
 * const original = { name: 'John', age: 30, nested: { value: 42 } };
 * const cloned = deepClone(original);
 * cloned.nested.value = 100; // original.nested.value is still 42
 * ```
 */
export function deepClone<T>(obj: T): T {
  // Use native structuredClone if available (Chrome 98+, Firefox 94+, Safari 15.4+)
  if (typeof structuredClone !== 'undefined') {
    return structuredClone(obj);
  }
  
  // Fallback for older browsers
  // Note: This loses functions, undefined values, and circular references
  return JSON.parse(JSON.stringify(obj));
}

